import React, { useEffect, useMemo, useRef, useState } from "react";
import Spinner from "Common/Spinner";

const getWorkerSrc = () => {
  const publicUrl = process.env.PUBLIC_URL || "";
  const basePath = publicUrl.endsWith("/") ? publicUrl.slice(0, -1) : publicUrl;
  return `${window.location.origin}${basePath}/pdf.worker.min.mjs`;
};

const isRemoteUrl = (url) => /^https?:\/\//i.test(url);

function PDFViewer({ fileUrl }) {
  const containerRef = useRef(null);
  const pagesRef = useRef(null);
  const renderTasksRef = useRef([]);
  const loadIdRef = useRef(0);
  const [loadState, setLoadState] = useState({ loading: false, error: null });

  const useIframe = useMemo(() => isRemoteUrl(fileUrl), [fileUrl]);

  useEffect(() => {
    const handleWheel = (e) => {
      if (
        (e.ctrlKey || e.metaKey) &&
        containerRef.current?.contains(e.target)
      ) {
        e.preventDefault();
      }
    };
    window.addEventListener("wheel", handleWheel, { passive: false });
    return () => window.removeEventListener("wheel", handleWheel);
  }, []);

  useEffect(() => {
    if (!fileUrl || useIframe) {
      setLoadState({ loading: false, error: null });
      return undefined;
    }

    const loadId = loadIdRef.current + 1;
    loadIdRef.current = loadId;
    let cancelled = false;
    const pagesContainer = pagesRef.current;

    const cancelRenderTasks = () => {
      renderTasksRef.current.forEach((task) => {
        try {
          task.cancel();
        } catch {
          // ignore cancel errors
        }
      });
      renderTasksRef.current = [];
    };

    const loadPdf = async () => {
      setLoadState({ loading: true, error: null });
      cancelRenderTasks();

      if (pagesContainer) {
        pagesContainer.innerHTML = "";
      }

      try {
        const pdfjsLib = await import("pdfjs-dist/build/pdf.mjs");
        pdfjsLib.GlobalWorkerOptions.workerSrc = getWorkerSrc();

        const response = await fetch(fileUrl);
        if (!response.ok) {
          throw new Error("Failed to fetch PDF");
        }

        const pdfData = await response.arrayBuffer();

        if (cancelled || loadIdRef.current !== loadId) {
          return;
        }

        const loadingTask = pdfjsLib.getDocument({
          data: pdfData,
          isEvalSupported: false,
        });
        const pdf = await loadingTask.promise;

        if (cancelled || loadIdRef.current !== loadId || !pagesRef.current) {
          return;
        }

        const containerWidth = pagesRef.current.clientWidth || 800;

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum += 1) {
          if (cancelled || loadIdRef.current !== loadId) {
            return;
          }

          const page = await pdf.getPage(pageNum);
          const baseViewport = page.getViewport({ scale: 1 });
          const scale = containerWidth / baseViewport.width;
          const viewport = page.getViewport({ scale });

          const canvas = document.createElement("canvas");
          canvas.className = "mx-auto mb-4 block w-full max-w-full h-auto bg-white";
          canvas.width = viewport.width;
          canvas.height = viewport.height;

          pagesRef.current.appendChild(canvas);

          const renderTask = page.render({
            canvasContext: canvas.getContext("2d"),
            viewport,
          });
          renderTasksRef.current.push(renderTask);
          await renderTask.promise;
        }

        if (!cancelled && loadIdRef.current === loadId) {
          setLoadState({ loading: false, error: null });
        }
      } catch {
        if (!cancelled && loadIdRef.current === loadId) {
          setLoadState({
            loading: false,
            error: "Unable to load the statement preview.",
          });
        }
      }
    };

    loadPdf();

    return () => {
      cancelled = true;
      cancelRenderTasks();
      if (pagesContainer) {
        pagesContainer.innerHTML = "";
      }
    };
  }, [fileUrl, useIframe]);

  if (!fileUrl) {
    return null;
  }

  if (useIframe) {
    return (
      <div
        ref={containerRef}
        className="border border-gray-200 rounded-md pdf-container-root"
      >
        <iframe
          title="PDF preview"
          src={`${fileUrl}#toolbar=1&navpanes=1`}
          className="w-full h-full border-0 bg-white"
        />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="border border-gray-200 rounded-md pdf-container-root"
    >
      <div className="relative h-full overflow-y-auto p-2">
        {loadState.loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#2e2e2e]/80">
            <Spinner size={32} />
          </div>
        )}
        {loadState.error && !loadState.loading && (
          <p className="p-4 text-sm text-center text-red-400">{loadState.error}</p>
        )}
        <div ref={pagesRef} />
      </div>
    </div>
  );
}

export default PDFViewer;
